import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { translations } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const ShopContext = createContext(null);

const getInitialLang = () => {
  // La lingua e' determinata dall'URL: /en/... => en, altrimenti it.
  // (LocaleProvider la mantiene sincronizzata durante la navigazione.)
  if (/^\/en(\/|$)/.test(window.location.pathname)) return "en";
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang === "it" || urlLang === "en") return urlLang;
  return "it";
};

export const ShopProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLang);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("idm_cart")) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("idm_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("idm_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
        ]);
        setProducts(prodRes.data);
        setCategories(catRes.data);
      } catch (e) {
        console.error("Failed to load shop data", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const t = useCallback((key) => translations[lang][key] || key, [lang]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image,
          priceSuffix: product.priceSuffix,
          qty,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setCart((prev) =>
      qty <= 0
        ? prev.filter((i) => i.id !== id)
        : prev.map((i) => (i.id === id ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <ShopContext.Provider
      value={{
        lang,
        setLang,
        t,
        products,
        categories,
        loading,
        cart,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used inside ShopProvider");
  return ctx;
};
